import React, { useState } from 'react';
import axios from 'axios';
import './saveCardNamer.css';
import CardBrandMark from './CardBrandMark';
import { cardLast4, cardBrandLabel } from '../ClientWallet/savedCardUtils';

/**
 * SaveCardNamer — "your card is saved; want to name it?" on a success screen.
 *
 * Naming happens at the moment of saving or not at all: a client will happily
 * type "Personal Visa" two seconds after paying, and never make a special trip
 * to Account → Payment methods to do it later (2026-08-13, when this first
 * appeared on the top-up success step).
 *
 * Lifted out of ClientDepositForm 2026-08-14 so the invoice payment page offers
 * exactly the same thing rather than a second copy of it — the surfaces differ
 * in what they paid for, not in how a card gets a nickname.
 *
 *   card     the row from GET /api/cards (id, scheme, masked_pan)
 *   onNamed  optional — the parent may want to know (e.g. to refresh a list)
 */
export default function SaveCardNamer({ card, onNamed }) {
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [named, setNamed] = useState('');
    const [error, setError] = useState('');

    if (!card?.id) return null;

    const save = async () => {
        const nickname = draft.trim();
        if (!nickname || saving) return;
        setSaving(true);
        setError('');
        try {
            await axios.patch(`/api/cards/${card.id}`, { nickname });
            setNamed(nickname);
            onNamed?.(nickname);
        } catch (e) {
            setError(e.response?.data?.error || 'Could not save the card name.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="scn">
            <div className="scn__head">
                <CardBrandMark scheme={card.scheme} />
                <span>Card saved — •••• {cardLast4(card.masked_pan)}</span>
            </div>

            {named ? (
                <div className="scn__done">Saved as “{named}”.</div>
            ) : (
                <>
                    <label className="scn__label" htmlFor={`scn-name-${card.id}`}>
                        Give it a name (optional) — easier to recognise at checkout
                    </label>
                    <div className="scn__row">
                        <input
                            id={`scn-name-${card.id}`}
                            className="scn__input"
                            type="text"
                            maxLength={40}
                            value={draft}
                            placeholder={`e.g. Personal ${cardBrandLabel(card.scheme)}`}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                            disabled={saving}
                        />
                        <button
                            type="button"
                            className="scn__btn"
                            onClick={save}
                            disabled={saving || !draft.trim()}
                        >
                            {saving ? 'Saving…' : 'Save name'}
                        </button>
                    </div>
                    {error && <div className="scn__error">{error}</div>}
                </>
            )}
        </div>
    );
}
